// src/components/NavBar.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeCtx } from "../App";
import {
  Home,
  PlusCircle,
  Boxes,
  ScanLine,
  Truck,
  Shield,
  Menu,
  X,
  BarChart3,
  UserRoundCog,
  UserRoundPenIcon,
  TractorIcon,
  MessagesSquare,
} from "lucide-react";
import MessageWindow from "./MessageWindow";

const NAV_ITEMS = [
  { to: "/new", label: "New", icon: PlusCircle },
  { to: "/inventory", label: "All Inventory", icon: Boxes },
  { to: "/admin/customers/details", label: "Customers", icon: UserRoundPenIcon },
  { to: "/Vendors", label: "Vendors", icon: TractorIcon },
  { to: "/vin", label: "VIN Scan / Upload", icon: ScanLine },
  { to: "/reports", label: "Inventory Reports", icon: BarChart3 },
  { to: "/admin", label: "Admin", icon: Shield },
  { to: "/driver", label: "Driver Portal", icon: Truck },
];

function useIsMobile(breakpoint = 840) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function safeParseUser() {
  try {
    return JSON.parse(localStorage.getItem("auth_user") || "null");
  } catch {
    return null;
  }
}

function userLabel(u) {
  if (!u) return "";
  const dn = String(u.display_name || "").trim();
  const em = String(u.email || "").trim();
  return dn || em || "";
}

export default function NavBar() {
  const nav = useNavigate();
  const loc = useLocation();
  const { theme, setTheme } = React.useContext(ThemeCtx);

  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const restoreTimer = useRef(null);

  const [authUser, setAuthUser] = useState(() => safeParseUser());

  // user popover
  const [userOpen, setUserOpen] = useState(false);
  const userBtnRef = useRef(null);
  const userPopRef = useRef(null);

  // messenger window (floating)
  const [msgOpen, setMsgOpen] = useState(false);

  // keep user dynamic (same tab + cross tab)
  useEffect(() => {
    const sync = () => setAuthUser(safeParseUser());

    const onStorage = (e) => {
      if (e.key === "auth_user" || e.key === "auth_token") sync();
    };
    window.addEventListener("storage", onStorage);

    // custom event (same-tab)
    window.addEventListener("auth_changed", sync);

    // fallback poll
    let last = localStorage.getItem("auth_user") || "";
    const t = setInterval(() => {
      const cur = localStorage.getItem("auth_user") || "";
      if (cur !== last) {
        last = cur;
        sync();
      }
    }, 800);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_changed", sync);
      clearInterval(t);
    };
  }, []);

  // IMPORTANT: do NOT close msgOpen on route change (it should stay up)
  useEffect(() => {
    setAuthUser(safeParseUser());
    setOpen(false);
    setUserOpen(false);
  }, [loc.pathname]);

  const displayName = useMemo(() => userLabel(authUser), [authUser]);

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  const go = (to) => {
    setOpen(false);
    setUserOpen(false);
    nav(to);
  };

  const logout = () => {
    setOpen(false);
    setUserOpen(false);
    setMsgOpen(false);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    window.dispatchEvent(new Event("auth_changed"));
    nav("/", { replace: true });
  };

  // hide nav on scroll
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      const dy = y - lastY.current;

      if (y < 20) setHidden(false);
      else if (dy > 8) setHidden(true);
      else if (dy < -8) setHidden(false);

      lastY.current = y;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const onMouseMove = (e) => {
      if (e.clientY <= 24) setHidden(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, [isMobile]);

  const onNavMouseLeave = () => {
    if (isMobile) return;
    if (restoreTimer.current) clearTimeout(restoreTimer.current);
    restoreTimer.current = setTimeout(() => {
      if ((window.scrollY || 0) > 20) setHidden(true);
    }, 650);
  };

  const onNavMouseEnter = () => {
    if (isMobile) return;
    if (restoreTimer.current) clearTimeout(restoreTimer.current);
    setHidden(false);
  };

  const navClass = useMemo(() => {
    let c = "nav2";
    if (hidden) c += " nav2-hidden";
    return c;
  }, [hidden]);

  // click-out closes user popover
  useEffect(() => {
    if (!userOpen) return;

    const onDown = (e) => {
      const btn = userBtnRef.current;
      const pop = userPopRef.current;
      if (!btn || !pop) return;
      if (btn.contains(e.target) || pop.contains(e.target)) return;
      setUserOpen(false);
    };

    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [userOpen]);

  const toggleUserPopover = () => setUserOpen((v) => !v);

  const openMessenger = () => {
    setUserOpen(false);
    setMsgOpen(true);
  };

  return (
    <>
      <div className={navClass} onMouseEnter={onNavMouseEnter} onMouseLeave={onNavMouseLeave}>
        <div className="nav2-wrap">
          <button
            className="nav2-home"
            onClick={() => go("/home")}
            title="Home"
            aria-label="Home"
            style={{ marginRight: 10 }}
            type="button"
          >
            <Home size={18} />
          </button>

          {!isMobile && (
            <div className="nav2-pills">
              {NAV_ITEMS.map((it) => {
                const Icon = it.icon;
                const active = isActive(it.to);
                return (
                  <button
                    key={it.to}
                    className={`nav2-pill ${active ? "nav2-pill-active" : ""}`}
                    onClick={() => go(it.to)}
                    title={it.label}
                    type="button"
                  >
                    <Icon size={16} />
                    <span>{it.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div style={{ marginLeft: "auto" }} />

          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
            {displayName ? (
              <>
                <button
                  ref={userBtnRef}
                  onClick={toggleUserPopover}
                  title={displayName}
                  className="nav2-pill"
                  type="button"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    borderRadius: 14,
                    background: userOpen ? "rgba(59,130,246,.14)" : "rgba(255,255,255,0.02)",
                    boxShadow: userOpen ? "0 0 0 1px rgba(59,130,246,.30)" : "0 0 0 1px rgba(255,255,255,0.06)",
                    border: "none",
                    cursor: "pointer",
                    maxWidth: 280,
                  }}
                >
                  <UserRoundCog size={18} style={{ flex: "0 0 auto" }} />
                  <span
                    style={{
                      fontWeight: 850,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1,
                    }}
                  >
                    {displayName}
                  </span>
                </button>

                {userOpen && (
                  <div
                    ref={userPopRef}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 10px)",
                      right: 0,
                      width: 260,
                      borderRadius: 16,
                      background: "var(--card-bg)",
                      color: "var(--fg)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                      border: "1px solid var(--card-border)",
                      padding: 12,
                      zIndex: 50,
                    }}
                  >
                    <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                      Signed in as <span style={{ fontWeight: 800 }}>{displayName}</span>
                    </div>

                    <button
                      type="button"
                      className="btn-outline"
                      onClick={openMessenger}
                      style={{
                        width: "100%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                      title="Open Messages"
                    >
                      <MessagesSquare size={16} />
                      Messages
                    </button>
                    <button
                      type="button"
                      className="btn-outline"
                      style={{
                        width: "100%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                      title="Open User Settings"
                    >
                      <MessagesSquare size={16} />
                      Settings
                    </button>
                  </div>
                )}
              </>
            ) : null}

            {!isMobile && displayName ? (
              <button className="nav2-home" onClick={logout} style={{ marginRight: 6 }} type="button">
                <span style={{ color: "orange", fontWeight: 900 }}>Logout</span>
              </button>
            ) : null}

            <select
              className="input"
              style={{ width: 140 }}
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              title="Theme"
            >
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>

            {isMobile && (
              <button className="nav2-menu" type="button" onClick={() => setOpen((v) => !v)} aria-label="Open menu">
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Floating messenger stays open across navigation */}
      <MessageWindow open={msgOpen} onClose={() => setMsgOpen(false)} user={authUser} />

      {/* MOBILE SHEET */}
      {isMobile && open && (
        <div className="nav2-sheet" onClick={() => setOpen(false)}>
          <div className="nav2-sheet-panel" onClick={(e) => e.stopPropagation()} style={{ width: "75%" }}>
            {NAV_ITEMS.map((it) => {
              const Icon = it.icon;
              const active = isActive(it.to);
              return (
                <button
                  key={it.to}
                  className={`nav2-sheet-item ${active ? "nav2-sheet-item-active" : ""}`}
                  onClick={() => go(it.to)}
                  style={{ fontSize: 18, padding: "20px 16px" }}
                  type="button"
                >
                  <Icon size={24} />
                  <span>{it.label}</span>
                </button>
              );
            })}

            <div style={{ height: 14 }} />

            {displayName ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 16px", fontWeight: 850 }}>
                  <UserRoundCog size={20} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayName}
                  </span>
                </div>

                <button
                  className="nav2-sheet-item"
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    openMessenger();
                  }}
                >
                  <MessagesSquare size={22} />
                  <span>Messages</span>
                </button>

                <button
                  className="nav2-sheet-item"
                  onClick={logout}
                  style={{ fontSize: 18, padding: "20px 16px" }}
                  type="button"
                >
                  <span style={{ fontWeight: 900, color: "orange" }}>Logout</span>
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
